<template>
    <div @click="stopAlert()" class="relative w-full overflow-hidden bg-gradient-to-br from-gray-800 via-gray-900 to-gray-950 ring-1 ring-white/15 rounded-xl">
        <template v-if="alert">
            <div :class="`alert-${alert}`" class="alert absolute z-[0] left-[-50%] top-[-100%] w-[200%] h-[300%] bg-no-repeat bg-center"></div>
            <div class="absolute z-[1] inset-[2px] rounded-[10px] bg-gradient-to-br from-gray-800 via-gray-900 to-gray-950"></div>
        </template>

        <div class="relative z-10 p-3 space-y-3">
            <div class="flex items-center justify-between space-x-2 ps-1">
                <p class="text-xs font-bold uppercase tracking-wide leading-5 text-gray-300">Call Center</p>
                <div class="flex items-center space-x-1">
                    <UBadge v-if="callsSummary.new" variant="solid" color="gray" size="xs" class="text-[11px]">
                        <span class="text-rose-500">•</span>&nbsp<span>{{ callsSummary.new }} New</span>
                    </UBadge>
                    <UBadge v-if="callsSummary.onHold" variant="solid" color="gray" size="xs" class="text-[11px]">
                        <span class="text-amber-500">•</span>&nbsp<span>{{ callsSummary.onHold }} On-hold</span>
                    </UBadge>
                    <UButton
                        v-if="!isAdmin"
                        @click="overlays.preferences = true"
                        icon="i-ph-gear-six-duotone"
                        color="gray"
                        variant="ghost"
                        size="xs"
                        class="shrink-0 p-0 !ms-2"
                    />
                </div>
            </div>
            <div v-if="calls.length" class="-m-0.5 p-0.5 space-y-2.5 max-h-72 overflow-auto">
                <CallCenterCall 
                    v-for="call in calls"
                    :key="call.id"
                    :call="call"
                />
            </div>
            <div v-else class="flex flex-col space-y-1 items-center justify-center p-6">
                <UIcon name="i-ph-phone-x-duotone" class="size-5 text-gray-500" />
                <span class="text-gray-500 text-[13px] italic">No active calls</span>
            </div>
            <div v-if="!isAdmin" class="flex items-center space-x-1">
                <UButton
                    @click="overlays.externalCall = true"
                    icon="i-heroicons-plus-20-solid"
                    label="New External Call"
                    color="white"
                    size="md"
                    block
                />
            </div>
        </div>
    </div>

    <CallCenterPreferencesModal v-model:open="overlays.preferences" />
    <CallCenterExternalCallModal v-model:open="overlays.externalCall" />
    
    <CallCenterIncomingCallModal 
        v-model:open="overlays.incomingCall"
        @ignore="onIgnoreIncomingCall()"
        @join="onJoinIncomingCall()"
        :customer="incomingCall?.customer"
    />
</template>

<script setup lang="ts">
import { Howl } from 'howler'
import { platform } from '@todesktop/client-core'
import { CallStatus, type Call, CallCenterAlert, CallSource } from '~/types/call'
import { UserRole } from '~/types/user'

const auth = useAuthStore()
const client = useSupabaseClient()

const { initCallCenter, state, getPreferences, joinCall } = useCallCenter()

const calls = ref<Call[]>([])
const incomingCall = ref<Call>()
const interval = ref<NodeJS.Timeout>()
const alertAudio = ref<{ play: () => void, stop: () => void, playing: () => boolean, unload: () => void }>()

const overlays = reactive({
    preferences: false,
    externalCall: false,
    incomingCall: false,
})

const isAdmin = computed(() => auth.user?.role === UserRole.admin)

const callsSummary = computed(() => ({
    new: calls.value.filter((call) => call.status === CallStatus.new).length,
    onHold: calls.value.filter((call) => call.status === CallStatus.onHold).length,
}))

const alert = computed(() => {
    if (calls.value.length >= 3) return CallCenterAlert.severe
    else if (calls.value.length >= 2) return CallCenterAlert.warning
    else if (calls.value.length >= 1) return CallCenterAlert.info
})

const unalertedCalls = computed(() => {
    return calls.value
        .filter((call) => {
            if (call.source !== CallSource.app) {
                return false
            }

            if (auth.hasRole(UserRole.operator, UserRole.doctor, UserRole.medicalDirector)) {
                return (call.status === CallStatus.new
                    || (call.status === CallStatus.active
                        && call.user_id === auth.user?.id
                        && call.transferrer_id !== null))
                    && !state.value.alertedCalls?.includes(call.id)
            }
        })
        .map((call) => call.id)
})

onMounted(async () => {
    await fetchCalls()

    client.channel('calls')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'calls' }, (payload) => {
            incomingCall.value = payload.new as Call
            fetchCalls()
        })
        .subscribe()

    interval.value = setInterval(
        () => fetchCalls(), 
        isAdmin.value ? 60000 : 5000
    )

    initCallCenter()
    initAlert()
})

onUnmounted(() => {
    if (interval.value) {
        clearInterval(interval.value)
    }

    if (alertAudio.value) {
        alertAudio.value.unload()
    }

    client.channel('calls').unsubscribe()
})

async function fetchCalls() {
    if (!auth.user) {
        throw Error("Unauthenticated user - skipping fetchCalls")
    }

    const { data } = await client.from('calls')
        .select(`
            id, number, user_id, transferrer_id, room_sid, status, source, created_at, on_hold_at, metadata,
            user:users!calls_user_id_fkey (
                id, name, role
            ), 
            customer:customers (
                id, first_name, middle_name, last_name, avatar, birth_date, gender, blood_type
            ), 
            address:addresses (
                id, city, region, country
            )
        `)
        .or([
            `status.eq.${CallStatus.new}`,
            `status.eq.${CallStatus.onHold}`,
            `and(status.eq.${CallStatus.active},user_id.eq.${auth.user.id})`,
        ].join(','))
        .order('created_at', { ascending: false })

    if (data) {
        calls.value = data as unknown as Call[]
        handleAlert()
    }
}

function initAlert() {
    if (alertAudio.value) {
        return
    }

    let isDesktop = false
    try {
        isDesktop = platform.todesktop.isDesktopApp()
    } catch {}

    if (isDesktop) {
        const howl = new Howl({
            src: ['todesktop-fs://ring.mp3'],
            html5: false,
            loop: true,
            volume: 1,
            rate: 0.75,
            autoplay: false
        })
            .on('play', () => {
                const preferences = getPreferences()
                if (preferences?.ringerDeviceId) {
                    const audioElement = (howl as any)?._sounds[0]._node
                    if (typeof audioElement.setSinkId === 'function') {
                        audioElement.setSinkId(preferences.ringerDeviceId)
                            .catch((error: any) => console.error('[DEBUG] CallCenter.vue - Failed to set audio output device', error))
                    }
                }
            })
            .on('loaderror', (_id: any, error: any) => {
                console.error('[DEBUG] CallCenter.vue - Ring tone load error', error)
            })
            .on('playerror', (_id: any, error: any) => {
                console.error('[DEBUG] CallCenter.vue - Ring tone play error', error)
            })

        alertAudio.value = {
            play: () => howl.play(),
            stop: () => howl.stop(),
            playing: () => howl.playing(),
            unload: () => howl.unload()
        }
    } else {
        const audio = new Audio('/audio/ring.mp3')
        audio.loop = true
        audio.volume = 1
        audio.playbackRate = 0.75
        audio.preload = 'auto'

        let isPlaying = false
        let pendingPlay = false

        const unlock = () => {
            audio.play().then(() => {
                if (pendingPlay) {
                    isPlaying = true
                    pendingPlay = false
                } else {
                    audio.pause()
                    audio.currentTime = 0
                }
            }).catch(() => {})
            document.removeEventListener('click', unlock)
            document.removeEventListener('touchstart', unlock)
        }

        document.addEventListener('click', unlock)
        document.addEventListener('touchstart', unlock)

        const applySinkId = () => {
            const preferences = getPreferences()
            if (preferences?.ringerDeviceId && typeof (audio as any).setSinkId === 'function') {
                (audio as any).setSinkId(preferences.ringerDeviceId).catch(() => {})
            }
        }

        alertAudio.value = {
            play: () => {
                applySinkId()
                audio.currentTime = 0
                audio.play()
                    .then(() => { isPlaying = true })
                    .catch((err: any) => {
                        if (err.name === 'NotAllowedError') {
                            pendingPlay = true
                        }
                    })
            },
            stop: () => {
                audio.pause()
                audio.currentTime = 0
                isPlaying = false
                pendingPlay = false
            },
            playing: () => isPlaying && !audio.paused,
            unload: () => {
                audio.pause()
                audio.src = ''
                isPlaying = false
                pendingPlay = false
                document.removeEventListener('click', unlock)
                document.removeEventListener('touchstart', unlock)
            }
        }
    }
}

function handleAlert() {
    if (unalertedCalls.value.length) {
        incomingCall.value = (incomingCall.value && unalertedCalls.value.includes(incomingCall.value.id))
            ? calls.value.find((c) => c.id === incomingCall.value?.id)
            : calls.value.find((c) => c.id === unalertedCalls.value.at(0))

        nextTick(() => {
            if (incomingCall.value) {
                overlays.incomingCall = true
            }

            if (!state.value.activeCallId && alertAudio.value && !alertAudio.value.playing()) {
                alertAudio.value.play()
            }
        })
    }
    else if (incomingCall.value) {
        const call = calls.value.find((c) => c.id === incomingCall.value?.id)
        if (!call || call.status === CallStatus.active) {
            incomingCall.value = undefined
            overlays.incomingCall = false
            stopAlert()
        }
    }
}

function stopAlert() {
    if (alertAudio.value) {
        alertAudio.value.stop()
    }

    if (unalertedCalls.value.length) {
        if (!state.value.alertedCalls) {
            state.value.alertedCalls = unalertedCalls.value
        }
        else {
            state.value.alertedCalls.push(...unalertedCalls.value)
        }
    }
}

function onJoinIncomingCall() {
    if (incomingCall.value) {
        stopAlert()
        joinCall(incomingCall.value)
        incomingCall.value = undefined
    }
}

function onIgnoreIncomingCall() {
    if (incomingCall.value) {
        incomingCall.value = undefined
        stopAlert()
    }
}
</script>

<style scoped>
@keyframes rotate { 100% { transform: rotate(1turn); } }
.alert { animation: rotate 2s linear infinite; }
.alert-severe { background-image: conic-gradient(transparent, theme('colors.red.600'), transparent 50%); }
.alert-warning { background-image: conic-gradient(transparent, theme('colors.yellow.600'), transparent 50%); }
.alert-info { background-image: conic-gradient(transparent, theme('colors.cyan.600'), transparent 50%); }
</style>
